import React, { useState } from 'react';
import { X, FileText, Calendar, User, MapPin, AlertTriangle } from 'lucide-react';
import { ComplianceRequirement } from '../../types/compliance';
import { formatDate } from '../../utils/formatters';

interface ComplianceDetailsProps {
  requirement: ComplianceRequirement;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<ComplianceRequirement>) => void;
}

export default function ComplianceDetails({
  requirement,
  onClose,
  onUpdate
}: ComplianceDetailsProps) {
  const [notes, setNotes] = useState(requirement.notes || '');

  const handleUpdateNotes = () => {
    onUpdate(requirement.id, { notes });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{requirement.title}</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700">Description</h4>
              <p className="mt-1 text-sm text-gray-600">{requirement.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>Due Date: {formatDate(requirement.dueDate)}</span>
                </div>
                {requirement.assignee && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mt-2">
                    <User className="w-4 h-4" />
                    <span>Assignee: {requirement.assignee}</span>
                  </div>
                )}
              </div>

              <div>
                {requirement.jurisdiction && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>Jurisdiction: {requirement.jurisdiction}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-sm mt-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    requirement.priority === 'high'
                      ? 'bg-red-100 text-red-800'
                      : requirement.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {requirement.priority.toUpperCase()} Priority
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Add notes..."
              />
              <button
                onClick={handleUpdateNotes}
                className="mt-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                Save Notes
              </button>
            </div>

            {requirement.documents && requirement.documents.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Related Documents</h4>
                <div className="space-y-2">
                  {requirement.documents.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 p-2 bg-gray-50 rounded"
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}